"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Session } from "@supabase/supabase-js";

// Create a context for Supabase
const SupabaseContext = createContext<{
  session: Session | null;
  isLoading: boolean;
  error: Error | null;
}>({
  session: null,
  isLoading: true,
  error: null,
});

// Hook to use Supabase context
export const useSupabase = () => useContext(SupabaseContext);

interface SupabaseProviderProps {
  children: React.ReactNode;
}

export default function SupabaseProvider({ children }: SupabaseProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);

        // Get initial session
        const {
          data: { session: initialSession },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        setSession(initialSession);

        // Listen for auth changes
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
          setSession(session);
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error initializing auth:", error);
        setError(
          error instanceof Error ? error : new Error("Unknown error occurred"),
        );
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // If there's an auth error, clear the session to prevent infinite retries
  useEffect(() => {
    if (error) {
      supabase.auth.signOut().catch((e) => {
        console.error("Error signing out after auth error:", e);
      });
    }
  }, [error]);

  return (
    <SupabaseContext.Provider value={{ session, isLoading, error }}>
      {children}
    </SupabaseContext.Provider>
  );
}
